import React, { useState, useEffect, useCallback } from 'react';
import { X, Users, Shield, Link2, Copy, Check, Globe, Lock, UserCheck, Loader2 } from 'lucide-react';
import { Button } from '../atoms/Button';
import { useServices, useToast } from '@/app/providers';
import { useFileSharing } from '@/shared/hooks';
import type { FileVisibility, UploadedFile } from '@yawi/types';
import type { User } from '@/shared/types';
import { UserRole } from '@yawi/types';

interface ShareDocumentModalProps {
  file: UploadedFile;
  onClose: () => void;
  onUpdated: (updatedFile: UploadedFile) => void;
}

const AVAILABLE_ROLES = [
  { value: UserRole.ADMIN, label: 'Administradores' },
  { value: UserRole.RESIDENT, label: 'Residentes' },
  { value: UserRole.GUARD, label: 'Guardias' },
];

export const ShareDocumentModal: React.FC<ShareDocumentModalProps> = ({
  file,
  onClose,
  onUpdated,
}) => {
  const toast = useToast();
  const { userRepo } = useServices();
  const {
    isUpdating,
    updateVisibility,
    shareWithUsers,
    shareWithRoles,
    generatePublicLink,
    revokePublicLink,
  } = useFileSharing();

  const [visibility, setVisibility] = useState<FileVisibility>(file.visibility || 'private');
  const [selectedUsers, setSelectedUsers] = useState<string[]>(file.sharedWithUsers || []);
  const [selectedRoles, setSelectedRoles] = useState<string[]>(file.sharedWithRoles || []);
  const [publicLink, setPublicLink] = useState<string | null>(
    file.publicToken ? `${window.location.origin}/public/files/${file.publicToken}` : null
  );
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [userSearch, setUserSearch] = useState('');
  const [copied, setCopied] = useState(false);

  // La búsqueda va al servidor: filtrar en memoria los primeros 100 usuarios
  // dejaba fuera a cualquiera más allá de esa página.
  const [debouncedUserSearch, setDebouncedUserSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedUserSearch(userSearch.trim()), 400);
    return () => clearTimeout(timer);
  }, [userSearch]);

  useEffect(() => {
    let active = true;
    setLoadingUsers(true);
    userRepo
      .getUsers({ limit: 100, search: debouncedUserSearch || undefined })
      .then((result) => {
        if (active) setUsers(result.data);
      })
      .catch(() => {
        if (active) toast.error('Error al cargar usuarios');
      })
      .finally(() => {
        if (active) setLoadingUsers(false);
      });
    return () => {
      active = false;
    };
  }, [userRepo, toast, debouncedUserSearch]);


  const handleVisibilityChange = useCallback(
    async (newVisibility: FileVisibility) => {
      setVisibility(newVisibility);
      const result = await updateVisibility(file.id, newVisibility);
      if (result) {
        onUpdated(result);
      }
    },
    [file.id, updateVisibility, onUpdated]
  );

  const handleUserToggle = useCallback(
    async (userId: string) => {
      const newSelected = selectedUsers.includes(userId)
        ? selectedUsers.filter((id) => id !== userId)
        : [...selectedUsers, userId];
      setSelectedUsers(newSelected);
      const result = await shareWithUsers(file.id, newSelected);
      if (result) {
        onUpdated(result);
      }
    },
    [file.id, selectedUsers, shareWithUsers, onUpdated]
  );

  const handleRoleToggle = useCallback(
    async (role: string) => {
      const newSelected = selectedRoles.includes(role)
        ? selectedRoles.filter((r) => r !== role)
        : [...selectedRoles, role];
      setSelectedRoles(newSelected);
      const result = await shareWithRoles(file.id, newSelected);
      if (result) {
        onUpdated(result);
      }
    },
    [file.id, selectedRoles, shareWithRoles, onUpdated]
  );

  const handleGeneratePublicLink = useCallback(async () => {
    const result = await generatePublicLink(file.id);
    if (result) {
      setPublicLink(result.url);
    }
  }, [file.id, generatePublicLink]);

  const handleRevokePublicLink = useCallback(async () => {
    const result = await revokePublicLink(file.id);
    if (result) {
      setPublicLink(null);
      onUpdated(result);
    }
  }, [file.id, revokePublicLink, onUpdated]);

  const handleCopyLink = useCallback(() => {
    if (publicLink) {
      navigator.clipboard.writeText(publicLink);
      setCopied(true);
      toast.success('Link copiado al portapapeles');
      setTimeout(() => setCopied(false), 2000);
    }
  }, [publicLink, toast]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex justify-between items-center p-5 border-b border-slate-100 bg-slate-50">
          <div>
            <h3 className="font-bold text-slate-800 text-lg">Compartir Documento</h3>
            <p className="text-sm text-slate-500 truncate max-w-xs">{file.originalName}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Visibility */}
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3">
              <Globe size={16} />
              Visibilidad
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => handleVisibilityChange('private')}
                disabled={isUpdating}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg border text-sm font-medium transition-all ${
                  visibility === 'private'
                    ? 'bg-slate-800 text-white border-slate-800'
                    : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'
                }`}
              >
                <Lock size={14} />
                Privado
              </button>
              <button
                type="button"
                onClick={() => handleVisibilityChange('shared')}
                disabled={isUpdating}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg border text-sm font-medium transition-all ${
                  visibility === 'shared'
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'
                }`}
              >
                <UserCheck size={14} />
                Compartido
              </button>
              <button
                type="button"
                onClick={() => handleVisibilityChange('public')}
                disabled={isUpdating}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg border text-sm font-medium transition-all ${
                  visibility === 'public'
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : 'bg-white text-slate-600 border-slate-300 hover:border-slate-400'
                }`}
              >
                <Globe size={14} />
                Público
              </button>
            </div>
          </div>

          {/* Roles Section */}
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3">
              <Shield size={16} />
              Compartir por Rol
            </label>
            <div className="space-y-2">
              {AVAILABLE_ROLES.map((role) => (
                <label
                  key={role.value}
                  className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selectedRoles.includes(role.value)}
                    onChange={() => handleRoleToggle(role.value)}
                    disabled={isUpdating}
                    className="w-4 h-4 text-primary-600 border-slate-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm text-slate-700">{role.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Users Section */}
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3">
              <Users size={16} />
              Compartir con Usuarios
            </label>
            <input
              type="text"
              placeholder="Buscar usuario..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm mb-3 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <div className="max-h-40 overflow-y-auto space-y-1 border border-slate-200 rounded-lg">
              {loadingUsers ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                </div>
              ) : users.length === 0 ? (
                <p className="text-center text-sm text-slate-500 py-4">No se encontraron usuarios</p>
              ) : (
                users.map((user) => (
                  <label
                    key={user.id}
                    className="flex items-center gap-3 p-2 hover:bg-slate-50 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => handleUserToggle(user.id)}
                      disabled={isUpdating}
                      className="w-4 h-4 text-primary-600 border-slate-300 rounded focus:ring-primary-500"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate">{user.name}</p>
                      <p className="text-xs text-slate-500 truncate">{user.email}</p>
                    </div>
                  </label>
                ))
              )}
            </div>
          </div>

          {/* Public Link Section */}
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3">
              <Link2 size={16} />
              Link Público
            </label>
            {publicLink ? (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={publicLink}
                    className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600 truncate"
                  />
                  <Button
                    variant="secondary"
                    onClick={handleCopyLink}
                    className="shrink-0"
                    icon={copied ? <Check size={16} /> : <Copy size={16} />}
                  >
                    {copied ? 'Copiado' : 'Copiar'}
                  </Button>
                </div>
                <Button
                  variant="secondary"
                  onClick={handleRevokePublicLink}
                  isLoading={isUpdating}
                  className="w-full text-red-600 hover:bg-red-50 border-red-200"
                >
                  Revocar Link Público
                </Button>
              </div>
            ) : (
              <Button
                variant="secondary"
                onClick={handleGeneratePublicLink}
                isLoading={isUpdating}
                icon={<Link2 size={16} />}
                className="w-full"
              >
                Generar Link Público
              </Button>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-100 bg-slate-50">
          <Button variant="primary" onClick={onClose} className="w-full">
            Listo
          </Button>
        </div>
      </div>
    </div>
  );
};
